
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatCard } from '@/components/dashboard/StatCard';
import { PayrollChart } from '@/components/dashboard/PayrollChart';
import { EmployeeStatusCard } from '@/components/dashboard/EmployeeStatusCard';
import { RecentPayslips } from '@/components/dashboard/RecentPayslips';
import { LeaveBalanceCard } from '@/components/dashboard/LeaveBalanceCard';
import { Users, DollarSign, CalendarClock, Briefcase } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { PayrollSummary, Payslip } from '@/types';
import { useEffect, useState } from 'react';

const Dashboard = () => {
  const { data, loading, refresh } = useAppDataContext();
  const { user } = useAuth();
  const { dashboardStats: stats, employees } = data;
  const [chartData, setChartData] = useState<PayrollSummary[]>([]);
  const [recentPayslips, setRecentPayslips] = useState<Payslip[]>([]);

  useEffect(() => {
    refresh.all();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCharts = async () => {
      if (user?.cid == null) return;
      try {
        const [summaryRes, payslipsRes] = await Promise.all([
          apiService.getPayrollSummary(user.cid),
          apiService.getAllPayslips(user.cid),
        ]);

        const summaryRaw = Array.isArray(summaryRes?.data)
          ? summaryRes.data
          : Array.isArray(summaryRes)
            ? summaryRes
            : [];

        const mappedSummary: PayrollSummary[] = summaryRaw
          .slice()
          .reverse()
          .slice(-6)
          .map((row: any) => ({
            period: String(row.period ?? ''),
            totalGrossSalary: Number(row.totalGrossSalary ?? row.total_gross ?? 0),
            totalDeductions: Number(row.totalDeductions ?? row.total_deductions ?? 0),
            totalNetSalary: Number(row.totalNetSalary ?? row.total_payroll ?? row.total_net ?? 0),
            employeesCount: Number(row.employeesCount ?? row.payslip_count ?? 0),
          }));
        setChartData(mappedSummary);

        const payslipsRaw = Array.isArray(payslipsRes?.data)
          ? payslipsRes.data
          : Array.isArray(payslipsRes)
            ? payslipsRes
            : [];

        const mappedPayslips: Payslip[] = payslipsRaw
          .slice()
          .sort((a: any, b: any) => String(b.period || '').localeCompare(String(a.period || '')))
          .slice(0, 5)
          .map((p: any) => ({
            id: String(p.id),
            employeeId: String(p.employee_id ?? p.employeeId ?? ''),
            period: p.period,
            payPeriod: p.period,
            issueDate: p.issue_date || p.issueDate || '',
            basicSalary: Number(p.basic_salary ?? p.basicSalary ?? 0),
            totalAllowances: Number(p.total_allowances ?? p.totalAllowances ?? 0),
            totalDeductions: Number(p.total_deductions ?? p.totalDeductions ?? 0),
            netSalary: Number(p.net_salary ?? p.netSalary ?? 0),
            status: (p.status || 'draft') as Payslip['status'],
            employeeName:
              p.employeeName ||
              [p.firstName, p.lastName].filter(Boolean).join(' ') ||
              'Employee',
          }));
        setRecentPayslips(mappedPayslips);
      } catch (err) {
        console.error('Failed to load dashboard charts/payslips', err);
      }
    };

    loadCharts();
  }, [user?.cid]);

  const resolvedStats = stats
    ? {
        totalEmployees: Number(stats.totalEmployees ?? 0),
        activeEmployees: Number(
          (stats as any).activeEmployees ??
            employees.filter((e) => e.status === 'active').length
        ),
        totalPayrollAmount: Number(
          (stats as any).totalPayrollAmount ?? (stats as any).totalPayroll ?? 0
        ),
        averageSalary: Number(
          (stats as any).averageSalary ??
            ((stats as any).totalPayrollAmount || (stats as any).totalPayroll
              ? Number((stats as any).totalPayrollAmount ?? (stats as any).totalPayroll) /
                Math.max(
                  Number((stats as any).activeEmployees ?? employees.filter((e) => e.status === 'active').length),
                  1
                )
              : 0)
        ),
      }
    : null;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-gray-500">Welcome to your payroll dashboard</p>
        </div>
        
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-payroll-primary"></div>
          </div>
        ) : (
          <>
            {resolvedStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total Employees"
                  value={resolvedStats.totalEmployees || 0}
                  icon={<Users className="h-6 w-6" />}
                  description="All registered employees"
                />
                <StatCard
                  title="Active Employees"
                  value={resolvedStats.activeEmployees || 0}
                  icon={<Briefcase className="h-6 w-6" />}
                  description="Currently working"
                />
                <StatCard
                  title="Monthly Payroll"
                  value={formatCurrency(resolvedStats.totalPayrollAmount || 0)}
                  icon={<DollarSign className="h-6 w-6" />}
                  description="Latest period net total"
                />
                <StatCard
                  title="Average Salary"
                  value={formatCurrency(resolvedStats.averageSalary || 0)}
                  icon={<CalendarClock className="h-6 w-6" />}
                  description="Per active employee"
                />
              </div>
            )}

            {!resolvedStats && !loading && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Dashboard figures could not be loaded. Check your connection and try refreshing.
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <PayrollChart data={chartData} />
              <EmployeeStatusCard employees={employees} />
              <LeaveBalanceCard />
            </div>

            <RecentPayslips payslips={recentPayslips} />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
